import useCreateBusiness from '../../lib/useCreateBusiness';
import { useEffect, useState } from 'react';
import TextInput from '../TextInput';
import IconSelectButton from '~/components/IconSelectButton';
import { useUserInfoQuery } from '~/lib/Queries';
import BreelyLogo from '~/components/icons/BreelyLogo';
import FullScreenModal from '~/components/FullScreenModal';
import { trackOnboardingStep } from '~/lib/AmplitudeTracking';
import Clarity from '@microsoft/clarity';
import { useAuth0 } from '@auth0/auth0-react';
import { PlusCircleIcon } from '@heroicons/react/24/outline/index';
import { XCircleIcon } from '@heroicons/react/24/outline';
import { saveOnboardingBookingUrls } from '~/lib/OnboardingImport';

const StepContainer = ({ title, description, children }) => {
    return (
        <div className="mx-auto sm:w-[600px] mt-4 sm:mt-20">
            <BreelyLogo className="mb-6" />
            <h1 className="mt-6">{title}</h1>
            <div className="text-lg mt-2 text-gray-500">{description}</div>
            <div className="mt-8">{children}</div>
        </div>
    );
};

const BusinessName = ({ value, setValue, onCancel, onNext, hideCancel }) => {
    const [error, setError] = useState('');
    const { data: userData, isLoading } = useUserInfoQuery();

    useEffect(() => {
        trackOnboardingStep('start', {});
    }, []);

    const showUserName =
        !isLoading &&
        userData &&
        (!userData.name || userData.name.indexOf('@') > -1);

    return (
        <StepContainer
            title="Your Business"
            description="Tell us your business name so we can tailor your scheduling experience."
        >
            <form
                onSubmit={ev => {
                    ev.preventDefault();
                    if (value?.name) onNext();
                }}
            >
                <TextInput
                    label={<span>Business Name <span className="text-red-500">*</span> </span>}
                    className="mt-6"
                    placeholder="My Business"
                    autoFocus={true}
                    required={true}
                    value={value.name || ''}
                    onChange={value => setValue('name', value)}
                />
                {!value.name && !!error && (
                    <div className="text-red-600">{error}</div>
                )}

                {showUserName && (
                    <TextInput
                        label="Your Name"
                        placeholder="Jane Doe"
                        className="mt-4"
                        required={true}
                        value={value.userName}
                        onChange={value => setValue('userName', value)}
                    />
                )}

                <div className="mt-4 flex items-center gap-x-4">
                    <button
                        className="btn-pill"
                        onClick={() => {
                            if (value.name) {
                                trackOnboardingStep('business_name', {
                                    has_user_name: !!value.userName,
                                });
                                onNext();
                            } else {
                                setError('Please enter a business name');
                            }
                        }}
                    >
                        Get Started
                    </button>
                    {!hideCancel && (
                        <button
                            className="btn-gray-pill"
                            onClick={onCancel}
                            type="button"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </form>
        </StepContainer>
    );
};

const BackButton = ({ onBack, disabled }) => (
    <button
        className="text-primary-700 hover:text-primary-800 flex items-center gap-2 text-lg"
        onClick={onBack}
        type="button"
        disabled={disabled}
    >
        <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M12 16l-6-6 6-6"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
        Back
    </button>
);

export const SWITCHING_FROM_NONE = 'None';

const DEFAULT_BOOKING_URL_PLACEHOLDER = 'https://example.com/booking';

// Add an `icon` (a logo from resources/js/components/icons) as they become
// available; options without one just show their name. `placeholder` is that
// platform's usual booking URL shape — leave it off when there isn't a well
// known one and the generic example is used instead.
const SWITCHING_FROM_OPTIONS = [
    { name: 'Acuity', placeholder: 'https://my-business.as.me/',
    icon: <svg
        width={65}
        height={24}
        viewBox="0 0 65 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M41.864 17.188a7.546 7.546 0 01-.092-.082c-.232-.21-.432-.441-.432-.865V6.403c0-.247-.094-.49-.3-.62-.205-.132-.478-.121-.78.034l-2.38 1.28-.002.001c-.127.074-.25.147-.341.233a.5.5 0 00-.169.375v.076c0 .176.085.317.174.431.046.058.098.116.15.172l.03.032c.043.046.085.093.128.142.207.237.398.524.398.987v6.695c0 .43-.184.663-.39.87l-.015.015-.014.014-.055.053c-.08.077-.165.16-.231.243a.732.732 0 00-.175.466v.102c0 .171.073.317.198.414.121.094.281.138.458.138h3.611c.177 0 .34-.044.465-.135a.507.507 0 00.215-.417v-.102a.7.7 0 00-.195-.472c-.073-.083-.167-.165-.256-.242zM36.123 17.374a1.225 1.225 0 00-.222-.096c-.014-.004-.027-.01-.04-.013-.06-.02-.115-.04-.171-.064a.661.661 0 01-.341-.3c-.094-.17-.167-.445-.167-.914V6.353a.663.663 0 00-.07-.317.457.457 0 00-.22-.202c-.17-.077-.398-.07-.65-.03l-2.544.434h-.003a.665.665 0 00-.388.215.622.622 0 00-.145.411v.077c0 .189.088.333.185.449.061.072.14.148.217.22v.002c.039.036.076.072.11.106.218.22.414.478.414.935v5.315c0 2.035-1.075 3.282-2.386 3.282-.72 0-1.264-.317-1.635-.948-.378-.64-.585-1.62-.585-2.947l.047-7v-.002a.615.615 0 00-.081-.325.488.488 0 00-.236-.197c-.179-.073-.415-.067-.669-.028l-2.498.435h-.003c-.31.06-.556.29-.556.626v.077c0 .187.085.33.18.447.06.072.137.148.212.221l.027.027.019.018c.01.012.022.023.033.034l.028.028c.211.218.403.477.403.936v4.421c0 1.895.477 3.333 1.298 4.298.821.967 1.97 1.441 3.279 1.441 1.531 0 2.701-.577 3.434-1.637.062.474.23.834.51 1.087.37.332.904.448 1.544.448.582 0 1.048-.032 1.373-.135a.908.908 0 00.407-.24.624.624 0 00.159-.432.677.677 0 00-.074-.33.52.52 0 00-.196-.2l.001.001zM64.828 6.083a.59.59 0 00-.388-.128h-2.638a.604.604 0 00-.412.145.537.537 0 00-.172.406v.077c0 .234.157.392.295.504.064.052.136.103.205.152l.03.021c.08.057.158.114.23.176.147.125.26.26.31.434.05.17.05.405-.083.743L59.4 15.715 56.409 8.33v-.001c-.118-.285-.148-.483-.138-.627a.661.661 0 01.124-.342c.034-.05.073-.098.116-.15l.011-.014h.001c.038-.046.08-.096.117-.148a.771.771 0 00.162-.465v-.077a.545.545 0 00-.166-.404.566.566 0 00-.394-.147h-3.755a.636.636 0 00-.395.125.497.497 0 00-.189.4v.103c0 .364.222.577.438.787l.033.03c.236.23.52.512.756 1.043l4.605 10.539c-.36.954-.78 1.865-1.217 2.54-.224.346-.45.624-.669.813-.22.19-.422.281-.605.281-.277 0-.458-.134-.607-.357-.145-.217-.25-.506-.366-.825l-.028-.078c-.124-.339-.264-.706-.483-.987a1.191 1.191 0 00-.987-.487c-.406 0-.74.149-.973.414-.23.263-.348.627-.348 1.032 0 1.274 1.339 2.672 3.364 2.672 1.256 0 2.187-.759 2.9-1.749.712-.986 1.224-2.225 1.639-3.231l4.49-10.579.001-.002c.213-.518.48-.8.705-1.032l.028-.03c.209-.214.421-.433.421-.794V6.48a.507.507 0 00-.172-.398zM51.32 14.834a.51.51 0 00-.312-.17.614.614 0 00-.63.346l-.012.023-.005.025c-.065.337-.22.633-.467.845-.247.212-.597.35-1.067.35-.348 0-.64-.146-.85-.423-.211-.281-.348-.713-.348-1.3V7.543h2.837a.583.583 0 00.584-.603v-.358c0-.348-.26-.628-.584-.628h-2.839l-.022-2.31v-.002c0-.198-.04-.383-.146-.521a.556.556 0 00-.462-.208h-.356c-.343 0-.576.299-.63.707-.093.719-.315 1.305-.65 1.707a1.695 1.695 0 01-1.358.628h-.736a.527.527 0 00-.415.178.68.68 0 00-.145.45v.358c0 .168.055.32.158.432.103.111.245.17.402.17h1.198v6.962c0 1.407.34 2.487.955 3.217.618.733 1.495 1.09 2.528 1.09 2.231 0 3.507-1.794 3.507-3.592a.579.579 0 00-.135-.386zM22.605 14.033a.626.626 0 00-.658.301l-.005.008-.004.01c-.504 1.059-1.456 2.003-2.926 2.003-1.225 0-2.237-.54-2.946-1.498-.713-.963-1.128-2.36-1.128-4.083 0-1.072.261-2.02.713-2.694.45-.671 1.088-1.074 1.863-1.074 1.097 0 1.632.771 2.124 1.632l.129.228c.198.352.398.708.63.994.288.353.64.618 1.124.647h.005c.47.014.818-.123 1.05-.368.23-.243.322-.572.322-.9 0-.931-.573-1.826-1.458-2.476-.89-.653-2.115-1.078-3.476-1.064-3.305.026-6.143 2.517-6.143 6.505 0 3.673 2.734 6.608 6.263 6.608 3 0 4.66-2.055 4.908-4.132a.637.637 0 00-.082-.435.485.485 0 00-.305-.212zM39.782 4.17c.615 0 1.137-.21 1.506-.583.37-.373.575-.898.575-1.502 0-.593-.206-1.117-.575-1.493C40.92.217 40.398 0 39.782 0c-.628 0-1.16.217-1.538.59-.378.376-.59.9-.59 1.495 0 .606.212 1.131.59 1.504.379.372.911.58 1.538.58zM11.629 17.429a1.535 1.535 0 00-.21-.11l-.03-.013a3.034 3.034 0 01-.182-.087 1.002 1.002 0 01-.384-.338c-.11-.167-.206-.417-.246-.81l-.475-4.907c-.17-1.679-.746-3.048-1.676-3.998-.93-.95-2.2-1.468-3.73-1.468-1.294 0-2.423.318-3.234.852-.81.534-1.32 1.296-1.32 2.178 0 .398.127.738.365.979.236.24.569.365.956.365.438 0 .73-.232.951-.55.172-.25.31-.566.444-.876l.095-.215c.172-.385.355-.746.622-1.013.26-.26.606-.438 1.12-.438.393 0 .71.09.974.262.263.171.48.43.662.788.366.721.573 1.815.703 3.296l.014.15c-.656-.437-1.535-.692-2.567-.692-1.225 0-2.348.339-3.167 1.012-.822.677-1.326 1.685-1.314 2.99 0 1.302.485 2.316 1.312 3.001.822.68 1.97 1.025 3.288 1.025 1.523 0 2.577-.55 3.204-1.368.088.427.262.735.543.94.354.258.849.326 1.457.326.515 0 1.012-.038 1.385-.143.185-.052.353-.124.477-.226a.557.557 0 00.22-.438.558.558 0 00-.075-.291.566.566 0 00-.182-.183zm-6.245.076c-1.35 0-2.267-1.388-2.267-3.077 0-.73.22-1.312.582-1.71.362-.399.879-.627 1.495-.627.593 0 1.094.216 1.457.595.363.38.6.932.645 1.626l.071.793c.066 1.298-.757 2.4-1.983 2.4z"
            fill="#1E1E1E"
        />
    </svg>},
    { name: 'Calendly', placeholder: 'https://calendly.com/my-biz', icon: <svg
            width={100}
            height={24}
            viewBox="0 0 100 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M58.884 9.47c1.538 0 2.995.944 3.303 2.982h-6.852c.286-1.789 1.62-2.982 3.549-2.982zm3.084 5.999c-.524.818-1.535 1.447-2.92 1.447-1.908 0-3.365-1.05-3.713-2.9h8.596a5.24 5.24 0 00.063-.802c0-2.88-2.008-5.43-5.109-5.43-3.2 0-5.375 2.365-5.375 5.41 0 3.082 2.196 5.41 5.498 5.41 2.052 0 3.611-.927 4.513-2.328l-1.553-.807zM52.665 3.32h-1.784v15.015h1.784V3.32zM73.924 12.02v6.315h-1.786v-6.212c0-1.686-.963-2.632-2.543-2.632-1.642 0-2.975.967-2.975 3.393v5.45h-1.785V8.053h1.785v1.48c.76-1.213 1.887-1.747 3.325-1.747 2.4 0 3.98 1.604 3.98 4.237M83.766 13.193c0-2.098-1.58-3.682-3.652-3.682-2.052 0-3.631 1.584-3.631 3.682s1.579 3.682 3.63 3.682c2.072 0 3.653-1.584 3.653-3.682zM85.55 3.32v15.015h-1.784v-1.77c-.82 1.259-2.134 2.038-3.878 2.038-2.852 0-5.19-2.366-5.19-5.41s2.338-5.41 5.19-5.41c1.744 0 3.057.781 3.878 2.036V3.32h1.784zM88.774 3.32H86.99v15.015h1.784V3.32zM47.7 13.193c0-2.098-1.58-3.682-3.653-3.682-2.05 0-3.63 1.584-3.63 3.682s1.58 3.682 3.63 3.682c2.071 0 3.652-1.584 3.652-3.682zm1.785-5.14v10.282h-1.786v-1.77c-.82 1.259-2.133 2.038-3.877 2.038-2.852 0-5.19-2.366-5.19-5.41s2.338-5.41 5.19-5.41c1.744 0 3.057.781 3.877 2.036V8.052h1.786z"
                fill="#006BFF"
            />
            <path
                d="M35.984 16.356a5.542 5.542 0 01-5.47-.938 5.568 5.568 0 01-1.93-5.215 5.575 5.575 0 011.194-2.619 5.555 5.555 0 015.198-1.946c.96.161 1.862.572 2.614 1.193.314.259.598.553.846.875l1.544-1.132a7.479 7.479 0 00-3.53-2.542 7.46 7.46 0 00-8.034 2.157 7.505 7.505 0 00-1.075 8.268 7.488 7.488 0 002.979 3.174 7.463 7.463 0 008.297-.574l-1.027-1.63a5.6 5.6 0 01-1.606.929zM97.39 8.052v5.837c0 1.887-1.096 3.082-2.767 3.082-1.672 0-2.882-1.195-2.882-3.082V8.052h-1.806v5.662c0 2.966 1.844 4.893 4.688 4.893 2.46 0 2.844-1.56 2.844-1.6v2.1c0 2.08-.903 3.256-2.786 3.256a2.769 2.769 0 01-1.832-.681 2.782 2.782 0 01-.93-1.722l-1.614.566a4.5 4.5 0 001.585 2.503 4.48 4.48 0 002.794.971c2.92 0 4.518-1.927 4.518-4.893V8.052h-1.813zM14.529 14.043c-.686.61-1.543 1.37-3.096 1.37h-.928c-1.124 0-2.146-.41-2.878-1.153-.715-.725-1.108-1.718-1.108-2.795V10.19c0-1.078.393-2.07 1.108-2.796.732-.742 1.754-1.15 2.878-1.15h.928c1.555 0 2.41.758 3.096 1.368.712.63 1.326 1.179 2.965 1.179.25 0 .499-.02.746-.06l-.006-.014a5.637 5.637 0 00-.344-.71l-1.095-1.901a5.614 5.614 0 00-4.872-2.82h-2.19a5.614 5.614 0 00-4.872 2.82L3.766 8.008a5.652 5.652 0 000 5.64l1.095 1.901a5.615 5.615 0 004.872 2.82h2.19a5.615 5.615 0 004.872-2.82l1.095-1.898c.13-.228.246-.466.344-.71l.006-.014a4.628 4.628 0 00-.746-.063c-1.639 0-2.253.547-2.965 1.18"
                fill="#006BFF"
            />
            <path
                d="M11.433 7.399h-.928c-1.711 0-2.835 1.224-2.835 2.792v1.274c0 1.568 1.124 2.792 2.835 2.792h.928c2.492 0 2.297-2.547 6.06-2.547.357 0 .713.032 1.063.098a5.646 5.646 0 000-1.96c-.35.066-.706.098-1.063.098-3.764 0-3.567-2.547-6.06-2.547z"
                fill="#006BFF"
            />
            <path
                d="M20.719 12.739a5.24 5.24 0 00-2.162-.932v.019a5.65 5.65 0 01-.314 1.098 4.11 4.11 0 011.787.738c0 .004-.003.01-.005.016a9.657 9.657 0 01-4.082 5.33 9.617 9.617 0 01-12.43-1.88 9.675 9.675 0 010-12.6 9.617 9.617 0 0112.43-1.88 9.657 9.657 0 014.082 5.33c0 .005.003.011.005.016a4.101 4.101 0 01-1.787.738c.141.354.247.723.314 1.099a.13.13 0 000 .017 5.239 5.239 0 002.162-.93c.616-.458.497-.975.403-1.28a10.824 10.824 0 00-4.574-5.978A10.779 10.779 0 002.614 3.765a10.844 10.844 0 000 14.126 10.778 10.778 0 0013.934 2.104c2.19-1.379 3.812-3.5 4.574-5.977.094-.306.213-.822-.403-1.28z"
                fill="#006BFF"
            />
            <path
                d="M18.24 8.732a4.633 4.633 0 01-.747.063c-1.638 0-2.252-.547-2.964-1.18-.686-.61-1.54-1.369-3.096-1.369h-.928c-1.125 0-2.146.41-2.878 1.152-.715.725-1.108 1.718-1.108 2.796v1.273c0 1.078.393 2.07 1.108 2.796.732.742 1.753 1.151 2.878 1.151h.928c1.555 0 2.41-.759 3.096-1.369.712-.629 1.326-1.179 2.964-1.179.25 0 .5.02.747.06a5.61 5.61 0 00.313-1.098v-.018a5.787 5.787 0 00-1.063-.098c-3.765 0-3.568 2.548-6.06 2.548h-.929c-1.71 0-2.834-1.225-2.834-2.793v-1.276c0-1.568 1.124-2.792 2.834-2.792h.93c2.491 0 2.296 2.547 6.06 2.547.356 0 .712-.032 1.062-.098v-.017a5.7 5.7 0 00-.314-1.1z"
                fill="#0AE8F0"
            />
            <path
                d="M18.24 8.732a4.633 4.633 0 01-.747.063c-1.638 0-2.252-.547-2.964-1.18-.686-.61-1.54-1.369-3.096-1.369h-.928c-1.125 0-2.146.41-2.878 1.152-.715.725-1.108 1.718-1.108 2.796v1.273c0 1.078.393 2.07 1.108 2.796.732.742 1.753 1.151 2.878 1.151h.928c1.555 0 2.41-.759 3.096-1.369.712-.629 1.326-1.179 2.964-1.179.25 0 .5.02.747.06a5.61 5.61 0 00.313-1.098v-.018a5.787 5.787 0 00-1.063-.098c-3.765 0-3.568 2.548-6.06 2.548h-.929c-1.71 0-2.834-1.225-2.834-2.793v-1.276c0-1.568 1.124-2.792 2.834-2.792h.93c2.491 0 2.296 2.547 6.06 2.547.356 0 .712-.032 1.062-.098v-.017a5.7 5.7 0 00-.314-1.1z"
                fill="#0AE8F0"
            />
        </svg> },
    { name: 'HoneyBook', icon: <svg
            width={90}
            height={11}
            viewBox="0 0 90 11"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <g clipPath="url(#clip0_10740_1179)" fill="#142127">
                <path d="M55.254 5.037c.943-.36 1.491-1.113 1.491-2.193 0-1.646-1.268-2.656-3.29-2.656h-4.746v10.075h4.865c2.125 0 3.29-.976 3.29-2.826 0-1.252-.547-2.056-1.61-2.399v-.001zm-3.719-2.741h1.577c.548 0 .856.36.856.994 0 .635-.308 1.01-.822 1.01h-1.61V2.296zm1.783 5.83h-1.783V6.106h1.748c.548 0 .856.394.856 1.01s-.308 1.01-.821 1.01v.002zM62.877 0c-2.998 0-5.26 2.262-5.26 5.227 0 2.965 2.26 5.225 5.26 5.225S68.12 8.19 68.12 5.227C68.12 2.263 65.876 0 62.878 0zm0 7.796c-1.422 0-2.398-1.078-2.398-2.57 0-1.49.976-2.552 2.398-2.552 1.423 0 2.382 1.063 2.382 2.553s-.978 2.57-2.382 2.57zM74.34 0c-2.999 0-5.262 2.262-5.262 5.227 0 2.965 2.262 5.225 5.261 5.225 3 0 5.242-2.262 5.242-5.225C79.581 2.263 77.338 0 74.34 0zm0 7.796c-1.424 0-2.4-1.078-2.4-2.57 0-1.49.976-2.552 2.4-2.552 1.422 0 2.38 1.063 2.38 2.553s-.977 2.57-2.38 2.57zM86.109 5.002L89.365.188h-3.17l-2.673 4.54V.188h-2.86v10.075h2.86V5.672l2.672 4.591h3.343l-3.428-5.26zM5.14 3.907H2.86V.188H0v10.075h2.86v-3.77h2.28v3.77h2.862V.188H5.139v3.719zM14.34 0c-2.998 0-5.261 2.262-5.261 5.227 0 2.965 2.261 5.225 5.26 5.225 3 0 5.244-2.262 5.244-5.225C19.583 2.263 17.34 0 14.34 0zm0 7.796c-1.423 0-2.399-1.078-2.399-2.57 0-1.49.977-2.552 2.398-2.552 1.421 0 2.382 1.063 2.382 2.553s-.976 2.57-2.382 2.57zM26.92 6.471l-1.196-2.684L24.005.188h-3.343v10.075h2.636L23.23 4.29l.988 2.135 1.912 3.838h3.338V.188h-2.617l.068 6.283zM34.043 6.327h3.923V4.066h-3.958v-1.48h4.078V.189h-6.904v10.075h6.942V7.848h-4.081v-1.52zM43.414 4.35L41.82.188h-3.049L41.959 7.3v2.963h2.895V7.3L48.04.188h-3.05L43.415 4.35z" />
            </g>
            <defs>
                <clipPath id="clip0_10740_1179">
                    <path fill="#fff" d="M0 0H90V10.4516H0z" />
                </clipPath>
            </defs>
        </svg> },
    {
        name: 'Mindbody',
        placeholder:
            'https://clients.mindbodyonline.com/classic/ws?studioid=12345',
        icon: <svg
            width={86}
            height={18}
            viewBox="0 0 86 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M15.238 8.144v5.974h-2.234V8.98c0-1.951-.678-2.668-2.075-2.668-1.396 0-2.194 1.016-2.194 2.847v4.958H6.501V8.979c0-1.951-.658-2.668-2.034-2.668-1.515 0-2.234 1.016-2.234 2.847v4.958H0V4.4h2.234v1.434h.04c.797-1.075 1.874-1.573 3.07-1.573 1.477 0 2.494.657 3.013 1.832.957-1.215 2.134-1.832 3.45-1.832 2.234 0 3.431 1.473 3.431 3.882v.001zm1.81 5.974h2.234V4.4h-2.234v9.718zM18.164 0c-.837 0-1.495.677-1.495 1.473 0 .797.658 1.493 1.495 1.493.837 0 1.516-.676 1.516-1.493C19.68.657 19.003 0 18.164 0zm8.62 4.26c-1.277 0-2.433.48-3.25 1.574h-.04V4.4H21.26v9.718h2.234V9.16c0-1.832.778-2.847 2.413-2.847 1.496 0 2.214.717 2.214 2.668v5.137h2.234V8.145c0-2.409-1.236-3.882-3.57-3.882V4.26zM39.316.2h2.214v13.918h-2.134v-1.395h-.04c-.698 1.036-1.755 1.554-3.151 1.554-2.693 0-4.728-1.972-4.728-4.979 0-3.006 2.034-5.037 4.708-5.037 1.277 0 2.373.418 3.091 1.334h.04V.199zm.08 9.06c0-1.891-1.057-2.987-2.832-2.987-1.556 0-2.812 1.155-2.812 2.987s1.157 2.987 2.792 2.987c1.635 0 2.853-.996 2.853-2.987h-.001zm14.04.04c0 3.006-2.034 4.978-4.727 4.978-1.396 0-2.453-.518-3.152-1.554h-.04v1.395h-2.133V.198h2.214v5.397h.04c.717-.916 1.815-1.334 3.09-1.334 2.673 0 4.708 2.11 4.708 5.037zm-2.273-.04c0-1.832-1.257-2.987-2.812-2.987-1.775 0-2.833 1.095-2.833 2.987 0 1.991 1.157 2.987 2.852 2.987 1.696 0 2.793-1.234 2.793-2.987zm13.356.023c-.002 2.945-2.115 4.995-5.106 4.995-2.991 0-5.106-2.15-5.106-4.998 0-2.848 2.02-5.003 5.074-5.018 2.823-.013 5.14 2.203 5.137 5.021h.001zm-2.273-.003c0-1.852-1.236-3.106-2.832-3.106-1.696 0-2.832 1.354-2.832 3.106 0 1.752 1.157 3.066 2.832 3.066 1.576 0 2.832-1.254 2.832-3.066zM73.23.199h2.214v13.918h-2.134v-1.395h-.04c-.698 1.036-1.755 1.554-3.151 1.554-2.692 0-4.727-1.972-4.727-4.979 0-3.006 2.034-5.037 4.707-5.037 1.277 0 2.374.418 3.091 1.334h.04V.199zm.08 9.06c0-1.891-1.057-2.987-2.832-2.987-1.556 0-2.812 1.155-2.812 2.987s1.157 2.987 2.792 2.987c1.635 0 2.853-.996 2.853-2.987h-.001zM83.767 4.4v4.64c0 1.832-.758 2.846-2.354 2.846-1.456 0-2.154-.716-2.154-2.668V4.4h-2.234v5.695c0 2.409 1.217 3.882 3.51 3.882 1.256 0 2.474-.518 3.191-1.533h.04v.796c0 1.713-.977 2.748-2.892 2.748-1.396 0-2.254-.398-3.311-.955l-.538 1.951C78.12 17.641 79.538 18 80.993 18 84.624 18 86 15.79 86 12.743V4.4h-2.234.001z"
                fill="#2D2D2D"
            />
        </svg>
    },
    { name: 'Vagaro', placeholder: 'https://www.vagaro.com/my-biz', icon: <svg
            width={78}
            height={18}
            viewBox="0 0 78 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M4.192 11.575L.109 1.429C-.178.738.11.259.873.259h1.146c.597 0 .98.262 1.17.833L6.098 9l2.91-7.908c.198-.568.58-.833 1.153-.833h1.17c.74 0 1.05.476.763 1.166l-4.082 10.15c-.215.524-.597.79-1.196.79h-1.43c-.573 0-.98-.266-1.194-.79zM12.845 6.311c0-3.524 2.41-6.311 5.922-6.311 1.6 0 3.08.595 4.107 2.073v-.787c0-.643.358-1.024 1.002-1.024h1.051c.645 0 1.026.381 1.026 1.024v10.075c0 .643-.382 1-1.026 1h-1.05c-.646 0-1.003-.357-1.003-1v-.81c-1.027 1.477-2.507 2.073-4.107 2.073-3.51-.001-5.921-2.788-5.921-6.313zm10.076 0c-.024-2.167-1.432-3.763-3.557-3.763-2.173 0-3.438 1.715-3.438 3.763 0 2.073 1.265 3.764 3.438 3.764 2.124 0 3.533-1.596 3.557-3.764zM29.317 16.832c-.549-.31-.645-.81-.358-1.382l.239-.428c.332-.596.788-.691 1.36-.358a6.34 6.34 0 003.224.858c2.197 0 3.724-1.215 3.724-3.692v-1.238c-1.002 1.477-2.53 2.073-4.13 2.073-3.51 0-5.897-2.834-5.897-6.311 0-3.454 2.387-6.312 5.897-6.312 1.6 0 3.104.596 4.107 2.073v-.861c0-.62.358-.953 1.003-.953h1.026c.645 0 1.002.381 1.002 1.024v10.53c0 4.359-3.056 6.145-6.566 6.145-1.98.002-3.437-.478-4.63-1.168zm8.214-10.48c-.025-2.167-1.433-3.786-3.558-3.786-2.173 0-3.438 1.741-3.438 3.787 0 2.073 1.265 3.787 3.438 3.787 2.125 0 3.533-1.62 3.558-3.787zM42.019 6.353c0-3.525 2.41-6.311 5.922-6.311 1.6 0 3.08.595 4.106 2.072v-.786c0-.644.359-1.024 1.003-1.024h1.05c.645 0 1.027.38 1.027 1.024v10.074c0 .643-.382 1-1.027 1h-1.05c-.645 0-1.002-.358-1.002-1v-.81c-1.027 1.476-2.507 2.072-4.108 2.072-3.51 0-5.921-2.787-5.921-6.311zm10.076 0c-.024-2.167-1.433-3.764-3.558-3.764-2.172 0-3.438 1.715-3.438 3.764 0 2.073 1.266 3.764 3.438 3.764 2.125-.001 3.534-1.597 3.558-3.764zM57.92 11.402V1.328c0-.644.358-1.024 1.002-1.024h1.003c.645 0 1.002.38 1.002 1.024v1.667c.406-2.073 1.958-2.953 3.2-2.953.16.005.319.02.477.047.525.047.62.476.62.952v1.191c0 .62-.286.881-.74.81a2.922 2.922 0 00-.62-.071c-1.146 0-2.865.69-2.865 4.643v3.787c0 .643-.383 1-1.027 1h-1.05c-.644.002-1.002-.356-1.002-1zM64.891 6.353c0-3.477 2.841-6.311 6.542-6.311C75.135.042 78 2.9 78 6.353c0 3.5-2.865 6.312-6.567 6.312-3.7 0-6.542-2.811-6.542-6.312zm10.123.024c0-2.096-1.579-3.763-3.581-3.763-2.003 0-3.582 1.668-3.582 3.763 0 2.073 1.58 3.715 3.582 3.715s3.581-1.644 3.581-3.716v.001z"
                fill="#CC4744"
            />
        </svg> },
    { name: 'Wix', placeholder: 'https://www.my-business.com/book-online', icon: <svg
            width={46}
            height={18}
            viewBox="0 0 46 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <path
                d="M46 0h-2.54c-.706 0-1.364.353-1.76.942L38.3 6.01a.229.229 0 01-.295.074.23.23 0 01-.082-.074L34.526.943a2.13 2.13 0 00-.763-.693 2.104 2.104 0 00-.996-.25h-2.541l6.053 9.026L30.26 18H32.8c.705 0 1.364-.353 1.76-.942l3.364-5.017a.23.23 0 01.19-.101.227.227 0 01.188.1l3.364 5.018c.195.29.457.528.764.693.307.164.65.25.996.249h2.54l-6.019-8.974L46 0zM24.998 1.841V18h1.215c.483 0 .946-.194 1.288-.54.341-.345.533-.813.533-1.301V0H26.82c-.483 0-.947.194-1.288.54a1.851 1.851 0 00-.534 1.301zM22.771 0H21.69c-.578 0-1.139.198-1.59.562a2.577 2.577 0 00-.895 1.442l-2.417 10.742L14.67 2.719C14.281.865 12.4-.384 10.473.153c-1.226.342-2.135 1.39-2.4 2.647l-2.095 9.933L3.565 2.005A2.582 2.582 0 002.67.563 2.535 2.535 0 001.08 0H0l4.05 18h1.535a2.93 2.93 0 001.857-.664 2.987 2.987 0 001.03-1.697l2.588-12.28a.33.33 0 01.32-.263.33.33 0 01.322.262l2.592 12.281c.14.668.504 1.267 1.03 1.697a2.93 2.93 0 001.856.663h1.54L22.77 0z"
                fill="#000"
            />
        </svg> },
    { name: 'Other' },
    { name: SWITCHING_FROM_NONE },
];

const SwitchingFrom = ({ value, setValue, onBack, onNext, isSaving }) => {
    const [focusedUrlIndex, setFocusedUrlIndex] = useState(0);
    const selected = value.switchingFrom || '';
    const bookingUrls = value.bookingUrls?.length ? value.bookingUrls : [''];
    const needsBookingUrls = !!selected && selected !== SWITCHING_FROM_NONE;
    const bookingUrlPlaceholder =
        SWITCHING_FROM_OPTIONS.find(option => option.name === selected)
            ?.placeholder || DEFAULT_BOOKING_URL_PLACEHOLDER;

    function selectOption(name) {
        setValue('switchingFrom', name);
        if (name === SWITCHING_FROM_NONE) {
            setValue('bookingUrls', []);
        } else if (!value.bookingUrls?.length) {
            setValue('bookingUrls', ['']);
        }
    }

    function setBookingUrl(index, url) {
        setValue(
            'bookingUrls',
            bookingUrls.map((existing, i) => (i === index ? url : existing)),
        );
    }

    function addBookingUrl(index) {
        setValue('bookingUrls', [
            ...bookingUrls.slice(0, index + 1),
            '',
            ...bookingUrls.slice(index + 1),
        ]);
        setFocusedUrlIndex(index + 1);
    }

    function removeBookingUrl(index) {
        setValue(
            'bookingUrls',
            bookingUrls.filter((_, i) => i !== index),
        );
        setFocusedUrlIndex(Math.max(0, index - 1));
    }

    return (
        <StepContainer
            title="Are You Switching?"
            description="Let us know if you're moving over from another platform so we can help bring your setup with you."
        >
            <form
                onSubmit={ev => {
                    ev.preventDefault();
                    if (!selected || isSaving) return;
                    trackOnboardingStep('switching_from', {
                        switching_from: selected,
                        booking_url_count: bookingUrls.filter(url => url.trim())
                            .length,
                    });
                    onNext();
                }}
            >
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    {SWITCHING_FROM_OPTIONS.map(option => (
                        <IconSelectButton
                            key={option.name}
                            className="h-16 bg-white flex flex-col items-center justify-center"
                            icon={option.icon}
                            label={option.icon ? '' : option.name}
                            isSelected={selected === option.name}
                            onClick={() => selectOption(option.name)}
                        />
                    ))}
                </div>

                {needsBookingUrls && (
                    <div className="mt-8">
                        <div className="ml-3 text-gray-500">
                            Enter the link(s) you give to clients to book their appointments
                        </div>
                        {bookingUrls.map((url, index) => (
                            <div
                                key={index}
                                className="mt-3 flex items-center gap-x-2"
                            >
                                <TextInput
                                    className="flex-1"
                                    placeholder={bookingUrlPlaceholder}
                                    autoFocus={index === focusedUrlIndex}
                                    value={url}
                                    onChange={newUrl =>
                                        setBookingUrl(index, newUrl)
                                    }
                                />
                                <button
                                    type="button"
                                    className={`rounded-full text-red-600 hover:bg-gray-100 hover:text-red-600 ${index === 0 ? 'invisible' : ''}`}
                                    title="Remove this booking URL"
                                    aria-label="Remove this booking URL"
                                    disabled={index === 0}
                                    onClick={() => removeBookingUrl(index)}
                                >
                                    <XCircleIcon className="w-6 h-6" />
                                </button>
                                <button
                                    type="button"
                                    className="rounded-full text-primary-700 hover:bg-gray-100 hover:text-primary-700"
                                    title="Add another booking URL"
                                    aria-label="Add another booking URL"
                                    onClick={() => addBookingUrl(index)}
                                >
                                    <PlusCircleIcon className="w-6 h-6" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="mt-8 flex items-center  gap-x-6">
                    <button
                        className="btn-pill disabled:opacity-50 disabled:cursor-not-allowed"
                        type="submit"
                        disabled={!selected || isSaving}
                    >
                        Next
                    </button>
                    <BackButton onBack={onBack} disabled={isSaving} />
                </div>
            </form>
        </StepContainer>
    );
};

const NewBusiness2025 = ({ isOpen, onClose }) => {
    const { create } = useCreateBusiness();
    const [value, setValue] = useState({});
    const [currentStep, setCurrentStep] = useState('business_name');
    const [isCreatingBusiness, setIsCreatingBusiness] = useState(false);
    const { user } = useAuth0();

    function setKeyValue(key, value) {
        setValue(old => ({ ...old, [key]: value }));
    }

    const canClose = typeof onClose === 'function';
    if (!canClose) {
        onClose = () => null;
    }

    useEffect(() => {
        if (!canClose && user?.sub) {
            // It's for a brand new business!
            Clarity.init('q5qtsg25j2');
            Clarity.identify(user?.sub);
        }
    }, [user]);

    useEffect(() => {
        if (!isOpen) {
            setValue({});
            setCurrentStep('business_name');
        }
    }, [isOpen]);

    function createBusiness() {
        if (isCreatingBusiness) return;

        const bookingUrls = (value.bookingUrls ?? [])
            .map(url => url.trim())
            .filter(url => !!url);

        // The assistant offers to import these as the new business's first prompt
        saveOnboardingBookingUrls(bookingUrls, value.switchingFrom);

        setIsCreatingBusiness(true);
        create({
            ...value,
            bookingUrls,
        })
            .then(result => {
                onClose();
            })
            .finally(() => {
                setIsCreatingBusiness(false);
            });
    }

    return (
        <FullScreenModal
            isOpen={isOpen}
            onClose={onClose}
            hideClose={!canClose}
            panelClassName="!bg-white"
        >
            {currentStep === 'business_name' && (
                <BusinessName
                    value={value}
                    setValue={setKeyValue}
                    onCancel={onClose}
                    hideCancel={!canClose}
                    onNext={() => setCurrentStep('switching_from')}
                />
            )}
            {currentStep === 'switching_from' && (
                <SwitchingFrom
                    value={value}
                    setValue={setKeyValue}
                    onBack={() => setCurrentStep('business_name')}
                    onNext={() => createBusiness()}
                    isSaving={isCreatingBusiness}
                />
            )}
        </FullScreenModal>
    );
};

export default NewBusiness2025;
